/**
 * Admins 账号管理页（E1 验收页）。
 *
 * Phase 1-6 / Plan §6.4 §6.5 / Design §5.2 / §9.3 离职 SOP / §13 多人审批 same-actor 静态校验.
 *
 * 功能：
 *  - 列表 + 状态/角色过滤
 *  - 顶部"新建账号"按钮（modal: unionId + nickname + role + isLegalRole）
 *  - 行内操作：改角色 / 停用 / 启用 / 强制踢出会话
 *  - 各操作按钮用 PermissionGate 包裹（不同权限点显示/隐藏）
 *  - 停用 success toast 含"已通知运维清浏览器缓存（§9.3）"提示
 *
 * 权限：admin:read / admin:create / admin:disable / admin:revoke_session
 *      仅 super_admin 持有，operator/support/finance/observer 看到 403 fallback。
 *
 * 注意：UI 隐藏不是安全边界 —— 真正校验在 admin-service requirePermission。
 */
import { useState, useMemo } from 'react';
import {
  Table,
  Form,
  Input,
  Button,
  Space,
  Tag,
  Select,
  Modal,
  Switch,
  Result,
  Card,
  message,
  Popconfirm,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { PERMISSIONS } from '../../contracts/permissions';
import type { AdminRole } from '../../contracts/roles';
import { roleLabel } from '../../utils/role-labels';
import { describeUserAgent, revokeReasonLabel } from '../../utils/login-history-labels';
import {
  listAdmins,
  createAdmin,
  updateAdmin,
  disableAdmin,
  enableAdmin,
  revokeAdminSession,
  listAdminLoginHistory,
  type AdminDto,
  type LoginRecordDto,
  type ListAdminsFilter,
  setAdminWecom,
} from '../../api/admins';

const ROLE_OPTIONS: { value: AdminRole; label: string }[] = [
  { value: 'super_admin', label: 'super_admin（超管）' },
  { value: 'operator', label: 'operator（运营）' },
  { value: 'support', label: 'support（客服）' },
  { value: 'finance', label: 'finance（财务）' },
  { value: 'observer', label: 'observer（只读观察员）' },
  { value: 'sales', label: 'sales（销售·只看名下客户）' },
  { value: 'ops_lead', label: 'ops_lead（运营主管·带客户+招销售+合伙人建档）' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'active' },
  { value: 'disabled', label: 'disabled' },
];

function statusColor(status: 'active' | 'disabled'): string {
  return status === 'active' ? 'green' : 'red';
}

const LOGIN_STATUS_TAG: Record<LoginRecordDto['status'], { color: string; text: string }> = {
  active: { color: 'green', text: '在线' },
  expired: { color: 'default', text: '已过期' },
  revoked: { color: 'orange', text: '已结束' },
};

function formatTime(v: string): string {
  return new Date(v).toLocaleString('zh-CN');
}

const loginHistoryColumns: ColumnsType<LoginRecordDto> = [
  {
    title: '登录时间',
    dataIndex: 'loginAt',
    key: 'loginAt',
    width: 170,
    render: (v: string) => formatTime(v),
  },
  { title: 'IP', dataIndex: 'ip', key: 'ip', width: 140, render: (v: string | null) => v ?? '-' },
  {
    title: '设备',
    dataIndex: 'userAgent',
    key: 'userAgent',
    width: 150,
    // 悬停看完整 UA，列里只放压缩后的标签
    render: (v: string | null) => <span title={v ?? undefined}>{describeUserAgent(v)}</span>,
  },
  {
    title: '续期',
    key: 'renew',
    width: 190,
    render: (_: unknown, r: LoginRecordDto) =>
      r.renewCount > 0 ? `${r.renewCount} 次，最近 ${formatTime(r.lastRenewedAt)}` : '-',
  },
  {
    title: '状态',
    key: 'status',
    render: (_: unknown, r: LoginRecordDto) => {
      const tag = LOGIN_STATUS_TAG[r.status];
      return (
        <Space size={4} wrap>
          <Tag color={tag.color}>{tag.text}</Tag>
          {r.status === 'revoked' && (
            <span title={r.revokeReason ?? undefined}>
              {revokeReasonLabel(r.revokeReason)}
              {r.revokedAt ? `（${formatTime(r.revokedAt)}）` : ''}
            </span>
          )}
        </Space>
      );
    },
  },
];

function AdminsPageInner() {
  const qc = useQueryClient();
  const [filterForm] = Form.useForm<{
    status?: 'active' | 'disabled';
    role?: AdminRole;
  }>();
  const [filters, setFilters] = useState<ListAdminsFilter>({});

  // 新建 modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm<{
    unionId: string;
    nickname: string;
    role: AdminRole;
    isLegalRole?: boolean;
  }>();

  // 改角色 modal
  const [editTarget, setEditTarget] = useState<AdminDto | null>(null);
  const [editForm] = Form.useForm<{ role?: AdminRole; isLegalRole?: boolean }>();

  // 停用 modal
  const [disableTarget, setDisableTarget] = useState<AdminDto | null>(null);
  const [disableForm] = Form.useForm<{ reason: string }>();

  // 登录记录 modal
  const [historyTarget, setHistoryTarget] = useState<AdminDto | null>(null);
  const historyQuery = useQuery({
    queryKey: ['admin-login-history', historyTarget?.id],
    queryFn: () => listAdminLoginHistory(historyTarget!.id),
    enabled: !!historyTarget,
  });

  // 强制踢出 modal
  const [revokeTarget, setRevokeTarget] = useState<AdminDto | null>(null);
  const [revokeForm] = Form.useForm<{ reason?: string }>();

  const queryParams = useMemo(() => filters, [filters]);
  const listQuery = useQuery({
    queryKey: ['admins', queryParams],
    queryFn: () => listAdmins(queryParams),
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['admins'] });
  }

  const createMutation = useMutation({
    mutationFn: createAdmin,
    onSuccess: () => {
      message.success('账号已创建');
      setCreateOpen(false);
      createForm.resetFields();
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? '创建失败');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; patch: { role?: AdminRole; isLegalRole?: boolean } }) =>
      updateAdmin(vars.id, vars.patch),
    onSuccess: () => {
      message.success('账号已更新');
      setEditTarget(null);
      editForm.resetFields();
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? '更新失败');
    },
  });

  // 企业微信成员绑定（顾问陪跑 PR⑦）
  const [wecomTarget, setWecomTarget] = useState<AdminDto | null>(null);
  const [wecomInput, setWecomInput] = useState('');
  const wecomMutation = useMutation({
    mutationFn: (vars: { id: string; wecomUserId: string | null }) => setAdminWecom(vars.id, vars.wecomUserId),
    onSuccess: () => {
      message.success('企业微信绑定已更新');
      setWecomTarget(null);
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? '更新失败');
    },
  });

  const disableMutation = useMutation({
    mutationFn: (vars: { id: string; reason: string }) => disableAdmin(vars.id, vars.reason),
    onSuccess: () => {
      // §9.3 离职 SOP 提示：占位文字，实际清缓存通知机制等运维系统接入
      message.success({
        content: '账号已停用。已通知运维清浏览器缓存（参考 §9.3 离职 SOP）',
        duration: 5,
      });
      setDisableTarget(null);
      disableForm.resetFields();
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? '停用失败');
    },
  });

  const enableMutation = useMutation({
    mutationFn: enableAdmin,
    onSuccess: () => {
      message.success('账号已恢复');
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? '恢复失败');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (vars: { id: string; reason?: string }) =>
      revokeAdminSession(vars.id, vars.reason),
    onSuccess: (data) => {
      message.success(`已强制踢出 ${data.revokedSessions} 个会话`);
      setRevokeTarget(null);
      revokeForm.resetFields();
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? '踢出失败');
    },
  });

  function applyFilters() {
    const v = filterForm.getFieldsValue();
    const next: ListAdminsFilter = {};
    if (v.status) next.status = v.status;
    if (v.role) next.role = v.role;
    setFilters(next);
  }

  function resetFilters() {
    filterForm.resetFields();
    setFilters({});
  }

  function openEdit(row: AdminDto) {
    setEditTarget(row);
    editForm.setFieldsValue({ role: row.role, isLegalRole: row.isLegalRole });
  }

  function submitEdit() {
    if (!editTarget) return;
    editForm.validateFields().then((v) => {
      const patch: { role?: AdminRole; isLegalRole?: boolean } = {};
      if (v.role && v.role !== editTarget.role) patch.role = v.role;
      if (typeof v.isLegalRole === 'boolean' && v.isLegalRole !== editTarget.isLegalRole) {
        patch.isLegalRole = v.isLegalRole;
      }
      if (Object.keys(patch).length === 0) {
        message.info('无变化');
        setEditTarget(null);
        return;
      }
      updateMutation.mutate({ id: editTarget.id, patch });
    });
  }

  function submitCreate() {
    createForm.validateFields().then((v) => {
      createMutation.mutate({
        unionId: v.unionId,
        nickname: v.nickname,
        role: v.role,
        isLegalRole: v.isLegalRole === true,
      });
    });
  }

  function submitDisable() {
    if (!disableTarget) return;
    disableForm.validateFields().then((v) => {
      disableMutation.mutate({ id: disableTarget.id, reason: v.reason });
    });
  }

  function submitRevoke() {
    if (!revokeTarget) return;
    revokeForm.validateFields().then((v) => {
      revokeMutation.mutate({ id: revokeTarget.id, reason: v.reason });
    });
  }

  const columns: ColumnsType<AdminDto> = [
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
      width: 140,
    },
    {
      title: 'unionId',
      dataIndex: 'unionId',
      key: 'unionId',
      width: 220,
      ellipsis: true,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 130,
      // 中文名为主，悬停（title）仍能看到 role 原值，方便跟下拉里的 role 码对上
      render: (r: AdminRole) => <Tag title={r}>{roleLabel(r)}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: 'active' | 'disabled') => <Tag color={statusColor(s)}>{s}</Tag>,
    },
    {
      title: '法务',
      dataIndex: 'isLegalRole',
      key: 'isLegalRole',
      width: 80,
      render: (v: boolean) => (v ? <Tag color="purple">法务</Tag> : '-'),
    },
    {
      title: '企业微信',
      dataIndex: 'wecomUserId',
      key: 'wecomUserId',
      width: 160,
      render: (v: string | null | undefined, row: AdminDto) => (
        <Space size={4}>
          <span>{v || '-'}</span>
          <PermissionGate point={PERMISSIONS.ADMIN_CREATE}>
            <Button
              size="small"
              type="link"
              onClick={() => {
                setWecomTarget(row);
                setWecomInput(v ?? '');
              }}
              data-testid={`admin-wecom-${row.id}`}
            >
              {v ? '修改' : '绑定'}
            </Button>
          </PermissionGate>
        </Space>
      ),
    },
    {
      title: '最近登录',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 170,
      render: (v: string | null) =>
        v ? new Date(v).toLocaleString('zh-CN') : '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 400,
      render: (_: unknown, row: AdminDto) => (
        <Space size="small" wrap>
          <PermissionGate point={PERMISSIONS.ADMIN_READ}>
            <Button
              size="small"
              onClick={() => setHistoryTarget(row)}
              data-testid={`admin-history-${row.id}`}
            >
              登录记录
            </Button>
          </PermissionGate>
          <PermissionGate point={PERMISSIONS.ADMIN_CREATE}>
            <Button size="small" onClick={() => openEdit(row)} data-testid={`admin-edit-${row.id}`}>
              改角色
            </Button>
          </PermissionGate>
          <PermissionGate point={PERMISSIONS.ADMIN_DISABLE}>
            {row.status === 'active' ? (
              <Button
                size="small"
                danger
                onClick={() => setDisableTarget(row)}
                data-testid={`admin-disable-${row.id}`}
              >
                停用
              </Button>
            ) : (
              <Popconfirm
                title="确认恢复该账号？"
                onConfirm={() => enableMutation.mutate(row.id)}
              >
                <Button size="small" data-testid={`admin-enable-${row.id}`}>
                  启用
                </Button>
              </Popconfirm>
            )}
          </PermissionGate>
          <PermissionGate point={PERMISSIONS.ADMIN_REVOKE_SESSION}>
            <Button
              size="small"
              danger
              onClick={() => setRevokeTarget(row)}
              data-testid={`admin-revoke-${row.id}`}
            >
              强制踢出
            </Button>
          </PermissionGate>
        </Space>
      ),
    },
  ];

  return (
    <Card title="账号管理（E1）" data-testid="admins-page">
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Form
          form={filterForm}
          layout="inline"
          data-testid="admins-filter-form"
          style={{ flexWrap: 'wrap', gap: 8 }}
        >
          <Form.Item name="status" label="状态">
            <Select
              placeholder="任意"
              allowClear
              style={{ width: 130 }}
              options={STATUS_OPTIONS}
            />
          </Form.Item>
          <Form.Item name="role" label="角色">
            <Select
              placeholder="任意"
              allowClear
              style={{ width: 200 }}
              options={ROLE_OPTIONS}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={applyFilters} data-testid="admins-search">
                查询
              </Button>
              <Button onClick={resetFilters} data-testid="admins-reset">
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
        <PermissionGate point={PERMISSIONS.ADMIN_CREATE}>
          <Button type="primary" onClick={() => setCreateOpen(true)} data-testid="admins-create-btn">
            新建账号
          </Button>
        </PermissionGate>
      </Space>

      <Table<AdminDto>
        rowKey="id"
        columns={columns}
        dataSource={listQuery.data?.items ?? []}
        loading={listQuery.isLoading}
        pagination={{ pageSize: 50, showTotal: (t) => `共 ${t} 条` }}
      />

      {/* 新建账号 modal */}
      <Modal
        title="新建账号"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={submitCreate}
        confirmLoading={createMutation.isPending}
        data-testid="admins-create-modal"
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="unionId"
            label="微信 unionId"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input placeholder="微信 unionId" />
          </Form.Item>
          <Form.Item
            name="nickname"
            label="昵称"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input placeholder="显示名（中文 / 英文均可）" />
          </Form.Item>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '必选' }]}
          >
            <Select options={ROLE_OPTIONS} />
          </Form.Item>
          <Form.Item name="isLegalRole" label="法务审批人（OPEN-16 L5 PII 审批用）" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* 改角色 modal */}
      <Modal
        title={`改角色：${editTarget?.nickname ?? ''}`}
        open={editTarget !== null}
        onCancel={() => setEditTarget(null)}
        onOk={submitEdit}
        confirmLoading={updateMutation.isPending}
        data-testid="admins-edit-modal"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="role" label="角色">
            <Select options={ROLE_OPTIONS} />
          </Form.Item>
          <Form.Item name="isLegalRole" label="法务审批人" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* 停用 modal */}
      <Modal
        title={`停用账号：${disableTarget?.nickname ?? ''}`}
        open={disableTarget !== null}
        onCancel={() => setDisableTarget(null)}
        onOk={submitDisable}
        confirmLoading={disableMutation.isPending}
        okButtonProps={{ danger: true }}
        data-testid="admins-disable-modal"
      >
        <p style={{ color: '#cf1322', marginBottom: 8 }}>
          停用将立即撤销其全部活跃会话 + 写访问令牌黑名单（最长 15 分钟内生效）。
        </p>
        <Form form={disableForm} layout="vertical">
          <Form.Item
            name="reason"
            label="停用原因（必填，将记入审计日志）"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input.TextArea rows={3} placeholder="例：离职 SOP / 违规操作 / 长期未登录" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 强制踢出 modal */}
      <Modal
        title={`强制踢出会话：${revokeTarget?.nickname ?? ''}`}
        open={revokeTarget !== null}
        onCancel={() => setRevokeTarget(null)}
        onOk={submitRevoke}
        confirmLoading={revokeMutation.isPending}
        okButtonProps={{ danger: true }}
        data-testid="admins-revoke-modal"
      >
        <p style={{ marginBottom: 8 }}>
          该 admin 全部活跃会话将立即失效（access token 写黑名单 15 分钟）。
        </p>
        <Form form={revokeForm} layout="vertical">
          <Form.Item name="reason" label="踢出原因（可选，将记入审计日志）">
            <Input.TextArea rows={2} placeholder="例：可疑登录 / 设备遗失" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`登录记录 · ${historyTarget?.nickname ?? ''}`}
        open={!!historyTarget}
        onCancel={() => setHistoryTarget(null)}
        footer={null}
        width={860}
        destroyOnHidden
      >
        <Table<LoginRecordDto>
          rowKey="sessionId"
          size="small"
          columns={loginHistoryColumns}
          dataSource={historyQuery.data?.items ?? []}
          loading={historyQuery.isLoading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          data-testid="admin-login-history-table"
          locale={{ emptyText: historyQuery.isError ? '加载失败，请稍后重试' : '暂无登录记录' }}
        />
        {historyQuery.data && historyQuery.data.total > historyQuery.data.items.length && (
          <Typography.Text type="secondary">
            仅显示最近 {historyQuery.data.items.length} 次，共 {historyQuery.data.total} 次登录。
          </Typography.Text>
        )}
        <div>
          <Typography.Text type="secondary">
            一次「登录」= 一次扫码或企微侧边栏登录；会话期间的自动续期合并计入「续期」。
            不含失败的登录尝试。
          </Typography.Text>
        </div>
      </Modal>

      <Modal
        title={`绑定企业微信 · ${wecomTarget?.nickname ?? ''}`}
        open={!!wecomTarget}
        onCancel={() => setWecomTarget(null)}
        onOk={() => wecomTarget && wecomMutation.mutate({ id: wecomTarget.id, wecomUserId: wecomInput.trim() || null })}
        okText={wecomInput.trim() ? '保存' : '解绑'}
        confirmLoading={wecomMutation.isPending}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            value={wecomInput}
            onChange={(e) => setWecomInput(e.target.value)}
            placeholder="企业微信成员账号（通讯录里的「账号」，如 ZhangSan）"
            maxLength={64}
          />
          <Typography.Text type="secondary">
            销售在企业微信聊天侧边栏打开「顾问助手」时，按这个账号识别是谁。留空保存 = 解绑。
          </Typography.Text>
        </Space>
      </Modal>
    </Card>
  );
}

export default function AdminsPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.ADMIN_READ}
      fallback={
        <Result
          status="403"
          title="无权访问"
          subTitle="账号管理页面仅 super_admin 可见，请联系超管申请权限。"
        />
      }
    >
      <AdminsPageInner />
    </PermissionGate>
  );
}
